import { createFileRoute, Link } from "@tanstack/react-router";
import { StickyHeader } from "@/components/site/StickyHeader";
import { Footer } from "@/components/site/Footer";
import { FadeIn } from "@/components/site/FadeIn";
import { BlogCover } from "@/components/site/BlogCover";
import { articles } from "@/lib/articles";

const posts = Object.values(articles)
  .map((a) => ({
    slug: a.slug,
    basePath: a.basePath ?? "/blog",
    title: a.title,
    description: a.metaDescription,
    date: a.date,
    readingTime: a.readingTime,
    image: a.image,
  }))
  .sort((a, b) => (a.date < b.date ? 1 : -1));

function PostLink({
  basePath,
  slug,
  className,
  children,
}: {
  basePath: string;
  slug: string;
  className?: string;
  children: React.ReactNode;
}) {
  return basePath === "/f" ? (
    <Link to="/f/$slug" params={{ slug }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/blog/$slug" params={{ slug }} className={className}>
      {children}
    </Link>
  );
}

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Insights on Growing an Advisory Firm | Katie Fernands" },
      {
        name: "description",
        content:
          "Essays on inbound marketing, LinkedIn, and pipeline systems for RIAs, banks, and fintech advisors.",
      },
      { property: "og:title", content: "Insights on Growing an Advisory Firm" },
      {
        property: "og:description",
        content:
          "Essays on inbound marketing, LinkedIn, and pipeline systems for financial advisors.",
      },
      { property: "og:url", content: "https://katiefernands.com/blog" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://katiefernands.com/blog" }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <StickyHeader />
      <main className="flex-1 pt-32 pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">Insights</p>
            <h1 className="text-4xl md:text-5xl font-serif tracking-tight">
              Notes on building a real pipeline
            </h1>
            <p className="mt-4 text-foreground/70 max-w-xl">
              Essays for RIAs, banks, and fintech advisors on inbound marketing, LinkedIn, and
              staying visible without cold calls.
            </p>
          </FadeIn>
          <div className="mt-16 space-y-10">
            {posts.map((p) => (
              <FadeIn key={p.slug}>
                <article className="border-t border-foreground/10 pt-8">
                  {p.image ? (
                    <PostLink basePath={p.basePath} slug={p.slug} className="block mb-5">
                      {p.image.cover ? (
                        <BlogCover cover={p.image.cover} className="block h-auto w-full rounded-sm" />
                      ) : (
                        <img
                          src={p.image.url}
                          alt={p.image.alt}
                          loading="lazy"
                          className="w-full rounded-sm object-cover aspect-[16/6]"
                        />
                      )}
                    </PostLink>
                  ) : null}
                  <p className="text-xs text-foreground/50 mb-2">
                    {new Date(p.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      timeZone: "UTC",
                    })}{" "}
                    · {p.readingTime}
                  </p>
                  <h2 className="text-2xl md:text-3xl font-serif tracking-tight">
                    <PostLink
                      basePath={p.basePath}
                      slug={p.slug}
                      className="hover:text-primary transition-colors"
                    >
                      {p.title}
                    </PostLink>
                  </h2>
                  <p className="mt-3 text-foreground/70">{p.description}</p>

                  <PostLink
                    basePath={p.basePath}
                    slug={p.slug}
                    className="mt-4 inline-block text-sm text-primary hover:underline"
                  >
                    Read the article →
                  </PostLink>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
